/**
 * Procedural map textures: flagstone floors, masonry walls with a visible face where the floor
 * runs below them, plank doors, and soft shadows where floors meet walls. They're drawn once into
 * small canvases (no image files) and stamped per square; the lighting pass shades them as before.
 */

const SIZE = 64;
const FLOOR_VARIANTS = 12;
const WALL_VARIANTS = 6;

const STONE = [52, 44, 36];
const GROUT = '#15110d';
const WALL_CAP = [92, 82, 72];
const WALL_FACE = [64, 56, 48];
const MORTAR = '#211b16';
const WOOD = [122, 79, 36];

/** mulberry32: a small seeded generator, so textures look the same every load. */
function seeded(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function surface(w = SIZE, h = SIZE): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function css([r, g, b]: number[], f = 1, a = 1): string {
  const k = (v: number) => Math.max(0, Math.min(255, Math.round(v * f)));
  return a === 1 ? `rgb(${k(r)},${k(g)},${k(b)})` : `rgba(${k(r)},${k(g)},${k(b)},${a})`;
}

/** Fine grain: a little per-pixel brightness noise over the whole texture. */
function grain(g: CanvasRenderingContext2D, amount: number, rand: () => number): void {
  const img = g.getImageData(0, 0, g.canvas.width, g.canvas.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (rand() - 0.5) * amount;
    d[i] += n;
    d[i + 1] += n;
    d[i + 2] += n;
  }
  g.putImageData(img, 0, 0);
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** A raised block: its face, a lit top-left edge and a shadowed bottom-right edge. */
function block(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string, r = 3): void {
  g.fillStyle = color;
  roundRect(g, x, y, w, h, r);
  g.fill();
  g.strokeStyle = 'rgba(255,240,220,0.07)';
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(x + 1, y + h - r);
  g.lineTo(x + 1, y + 1);
  g.lineTo(x + w - r, y + 1);
  g.stroke();
  g.strokeStyle = 'rgba(0,0,0,0.32)';
  g.beginPath();
  g.moveTo(x + r, y + h - 0.5);
  g.lineTo(x + w - 0.5, y + h - 0.5);
  g.lineTo(x + w - 0.5, y + r);
  g.stroke();
}

/** Flagstones: two to four worn stones per square, with grout between them. */
function floorTile(seed: number): HTMLCanvasElement {
  const [c, g] = surface();
  const r = seeded(seed);
  g.fillStyle = GROUT;
  g.fillRect(0, 0, SIZE, SIZE);
  const sx = Math.round(18 + r() * 28), sy = Math.round(18 + r() * 28);
  const layouts: [number, number, number, number][][] = [
    [[0, 0, sx, sy], [sx, 0, SIZE - sx, sy], [0, sy, sx, SIZE - sy], [sx, sy, SIZE - sx, SIZE - sy]],
    [[0, 0, sx, sy], [sx, 0, SIZE - sx, sy], [0, sy, SIZE, SIZE - sy]],
    [[0, 0, sx, SIZE], [sx, 0, SIZE - sx, sy], [sx, sy, SIZE - sx, SIZE - sy]],
    [[0, 0, SIZE, sy], [0, sy, SIZE, SIZE - sy]],
    [[0, 0, SIZE, SIZE]],
  ];
  const layout = layouts[Math.floor(r() * r() * layouts.length * 1.25) % layouts.length];
  for (const [x, y, w, h] of layout) {
    const tone = 0.84 + r() * 0.3;
    const warm = (r() - 0.5) * 0.08;
    block(g, x + 1.5, y + 1.5, w - 3, h - 3, css([STONE[0] * (1 + warm), STONE[1], STONE[2] * (1 - warm)], tone), 3);
    // Worn patches.
    for (let k = 0; k < 2; k++) {
      const px = x + 4 + r() * Math.max(1, w - 8), py = y + 4 + r() * Math.max(1, h - 8), rad = 4 + r() * 9;
      const grad = g.createRadialGradient(px, py, 0, px, py, rad);
      grad.addColorStop(0, r() < 0.5 ? 'rgba(0,0,0,0.16)' : 'rgba(255,235,210,0.05)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grad;
      g.fillRect(px - rad, py - rad, rad * 2, rad * 2);
    }
  }
  if (r() < 0.3) {
    // A hairline crack across one stone.
    g.strokeStyle = 'rgba(0,0,0,0.45)';
    g.lineWidth = 1;
    g.beginPath();
    let px = 6 + r() * 52, py = 6 + r() * 52;
    g.moveTo(px, py);
    for (let k = 0; k < 4; k++) {
      px += (r() - 0.5) * 18;
      py += (r() - 0.3) * 14;
      g.lineTo(px, py);
    }
    g.stroke();
  }
  if (r() < 0.18) {
    // Moss in the grout.
    for (let k = 0; k < 14; k++) {
      g.fillStyle = `rgba(${70 + r() * 30},${100 + r() * 40},${50 + r() * 20},${0.25 + r() * 0.3})`;
      const along = r() < 0.5;
      g.fillRect(along ? r() * SIZE : sx - 1 + r() * 2, along ? sy - 1 + r() * 2 : r() * SIZE, 1.5, 1.5);
    }
  }
  grain(g, 16, r);
  return c;
}

/** Masonry: a rough stone cap seen from above, and a brick face where the floor runs below it. */
function wallTile(seed: number, face: boolean): HTMLCanvasElement {
  const [c, g] = surface();
  const r = seeded(seed);
  const capH = face ? Math.round(SIZE * 0.6) : SIZE;
  g.fillStyle = MORTAR;
  g.fillRect(0, 0, SIZE, SIZE);
  // Cap: two rows of large cut stones, offset like brickwork.
  const rows = 2;
  const rowH = capH / rows;
  for (let row = 0; row < rows; row++) {
    const offset = row % 2 ? -SIZE / 4 : 0;
    for (let x = offset; x < SIZE; x += SIZE / 2) {
      const tone = 0.86 + r() * 0.24;
      block(g, x + 1, row * rowH + 1, SIZE / 2 - 2, rowH - 2, css(WALL_CAP, tone), 2);
    }
  }
  if (face) {
    // The wall's face: courses of smaller bricks, darker, with a lit lip along the top.
    const faceH = SIZE - capH;
    const courses = 3;
    const ch = faceH / courses;
    for (let k = 0; k < courses; k++) {
      const offset = k % 2 ? -8 : 0;
      for (let x = offset; x < SIZE; x += 16) {
        const tone = 0.8 + r() * 0.25;
        g.fillStyle = css(WALL_FACE, tone);
        g.fillRect(x + 1, capH + k * ch + 1, 14, ch - 1.5);
      }
    }
    const shade = g.createLinearGradient(0, capH, 0, SIZE);
    shade.addColorStop(0, 'rgba(0,0,0,0)');
    shade.addColorStop(1, 'rgba(0,0,0,0.45)');
    g.fillStyle = shade;
    g.fillRect(0, capH, SIZE, faceH);
    g.fillStyle = 'rgba(255,235,205,0.16)';
    g.fillRect(0, capH - 1, SIZE, 2);
  }
  grain(g, 18, r);
  return c;
}

/**
 * A plank door in its stone frame, `length` squares wide: posts only at the two ends, planks and
 * iron bands right across, and one ring pull. Open, its leaves stand swung back at the ends (two
 * leaves for a double door). A locked door shows one padlock at its middle. `vertical` doors span
 * a passage running east to west; the strip is then `length` squares tall.
 */
function doorStrip(open: boolean, vertical: boolean, length: number, locked: boolean): HTMLCanvasElement {
  const span = SIZE * length;
  const [c, g] = vertical ? surface(SIZE, span) : surface(span, SIZE);
  const r = seeded((open ? 71 : 37) + (length - 1) * 101);
  g.save();
  if (vertical) {
    // Draw it as a horizontal door, turned a quarter.
    g.translate(SIZE, 0);
    g.rotate(Math.PI / 2);
  }
  const post = 8, thick = open ? 9 : 22, mid = SIZE / 2, centre = span / 2;
  block(g, 0, mid - 14, post, 28, css(WALL_CAP, 0.95), 2);
  block(g, span - post, mid - 14, post, 28, css(WALL_CAP, 0.9), 2);
  if (open) {
    block(g, post - 2, mid - 14 - 30, thick, 34, css(WOOD, 0.8), 2);
    if (length > 1) block(g, span - post - thick + 2, mid - 14 - 30, thick, 34, css(WOOD, 0.76), 2);
  } else {
    const y = mid - thick / 2;
    const planks = 4 * length, w = (span - post * 2) / planks;
    for (let k = 0; k < planks; k++) block(g, post + k * w, y, w - 1, thick, css(WOOD, 0.85 + r() * 0.25), 1);
    g.fillStyle = '#26211c';
    g.fillRect(post + 6, y + 3, span - post * 2 - 12, 3);
    g.fillRect(post + 6, y + thick - 6, span - post * 2 - 12, 3);
    if (length > 1) g.fillRect(centre - 1, y + 1, 2, thick - 2); // where the two leaves meet
    g.strokeStyle = '#c9a24a';
    g.lineWidth = 1.5;
    g.beginPath();
    g.arc(centre + 10, mid, 3.5, 0, Math.PI * 2);
    g.stroke();
  }
  g.restore();
  grain(g, 10, r);
  if (locked) {
    const cx = c.width / 2, cy = c.height / 2, w = SIZE * 0.32, h = SIZE * 0.26;
    g.strokeStyle = '#f5c542';
    g.lineWidth = SIZE / 18;
    g.beginPath();
    g.arc(cx, cy - h * 0.2, w * 0.38, Math.PI, 0);
    g.stroke();
    g.fillStyle = '#f5c542';
    g.fillRect(cx - w / 2, cy - h * 0.2, w, h);
  }
  return c;
}

/** Where square `index` of a door strip sits in its texture (source x, y; each slice is TEXTURE_SIZE square). */
export function doorSlice(vertical: boolean, index: number): [number, number] {
  return vertical ? [0, index * SIZE] : [index * SIZE, 0];
}

export const TEXTURE_SIZE = SIZE;

/** Neighbour bits for wall shadows: N, E, S, W, then the four corners. */
const N = 1, E = 2, S = 4, W = 8, NE = 16, SE = 32, SW = 64, NW = 128;

function shadowTile(mask: number): HTMLCanvasElement {
  const [c, g] = surface();
  const depth = SIZE * 0.34;
  const edge = (x0: number, y0: number, x1: number, y1: number, rect: [number, number, number, number]) => {
    const grad = g.createLinearGradient(x0, y0, x1, y1);
    grad.addColorStop(0, 'rgba(0,0,0,0.55)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect(...rect);
  };
  if (mask & N) edge(0, 0, 0, depth, [0, 0, SIZE, depth]);
  if (mask & S) edge(0, SIZE, 0, SIZE - depth, [0, SIZE - depth, SIZE, depth]);
  if (mask & W) edge(0, 0, depth, 0, [0, 0, depth, SIZE]);
  if (mask & E) edge(SIZE, 0, SIZE - depth, 0, [SIZE - depth, 0, depth, SIZE]);
  const corner = (cx: number, cy: number) => {
    const grad = g.createRadialGradient(cx, cy, 0, cx, cy, depth);
    grad.addColorStop(0, 'rgba(0,0,0,0.45)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect(cx - depth, cy - depth, depth * 2, depth * 2);
  };
  if (mask & NE && !(mask & (N | E))) corner(SIZE, 0);
  if (mask & SE && !(mask & (S | E))) corner(SIZE, SIZE);
  if (mask & SW && !(mask & (S | W))) corner(0, SIZE);
  if (mask & NW && !(mask & (N | W))) corner(0, 0);
  return c;
}

/** A stable pseudo-random index per square. */
function pick(x: number, y: number, n: number): number {
  const v = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return Math.floor((v - Math.floor(v)) * n);
}

export class TileTextures {
  private floors: HTMLCanvasElement[] = [];
  private walls: HTMLCanvasElement[] = [];
  private faces: HTMLCanvasElement[] = [];
  private doors = new Map<string, HTMLCanvasElement>();
  private shadows = new Map<number, HTMLCanvasElement>();

  constructor() {
    for (let i = 0; i < FLOOR_VARIANTS; i++) this.floors.push(floorTile(1000 + i * 17));
    for (let i = 0; i < WALL_VARIANTS; i++) {
      this.walls.push(wallTile(2000 + i * 31, false));
      this.faces.push(wallTile(3000 + i * 29, true));
    }
  }

  floor(x: number, y: number): HTMLCanvasElement {
    return this.floors[pick(x, y, FLOOR_VARIANTS)];
  }

  /** A wall square; `face` when the square below it is open floor. */
  wall(x: number, y: number, face: boolean): HTMLCanvasElement {
    return (face ? this.faces : this.walls)[pick(x, y, WALL_VARIANTS)];
  }

  /** A whole door strip (`length` squares long); draw one square of it with `doorSlice`. */
  door(open: boolean, vertical: boolean, length = 1, locked = false): HTMLCanvasElement {
    const key = `${open}:${vertical}:${length}:${locked}`;
    let c = this.doors.get(key);
    if (!c) this.doors.set(key, (c = doorStrip(open, vertical, length, locked)));
    return c;
  }

  /** Soft shadow on a floor square from the walls around it (see `wallMask`). */
  shadow(mask: number): HTMLCanvasElement | null {
    if (!mask) return null;
    let c = this.shadows.get(mask);
    if (!c) this.shadows.set(mask, (c = shadowTile(mask)));
    return c;
  }
}

/** Which of a square's eight neighbours are walls, as bits for `TileTextures.shadow`. */
export function wallMask(isWall: (dx: number, dy: number) => boolean): number {
  return (isWall(0, -1) ? N : 0) | (isWall(1, 0) ? E : 0) | (isWall(0, 1) ? S : 0) | (isWall(-1, 0) ? W : 0)
    | (isWall(1, -1) ? NE : 0) | (isWall(1, 1) ? SE : 0) | (isWall(-1, 1) ? SW : 0) | (isWall(-1, -1) ? NW : 0);
}
